import { PageRouteEnum } from '@app/constants/pages';

/**
 * Checks if the current path contains an entity UUID parameter
 * The path should match pattern: /login/entity/:entityUUID
 *
 * @param pathname - The current location pathname
 * @returns True if the path contains /entity/ followed by a UUID-like string
 */
export const hasEntityUUID = (pathname: string): boolean => {
	// Match pattern: /entity/{UUID-like string}
	// UUID pattern: 8-4-4-4-12 hex characters OR 24 hex characters (MongoDB ObjectId)
	const entityUUIDPattern =
		/\/entity\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[a-f0-9]{24})/i;
	return entityUUIDPattern.test(pathname);
};

/**
 * Validates and processes an internal redirect path for security
 * Only processes redirect if the current path contains an entity UUID
 *
 * @param redirectParam - The redirect parameter from URL query string (should be an internal path)
 * @param hash - The current location hash to append to redirect
 * @param pathname - The current location pathname to check for entity UUID
 * @param skipEntityCheck - Skip entity UUID validation (used when reading from sessionStorage)
 * @returns The validated redirect path
 */
export const getSecureRedirectPath = (
	redirectParam: string | null,
	hash: string = '',
	pathname: string = '',
	skipEntityCheck: boolean = false,
): string => {
	const defaultPath = `/${PageRouteEnum.home}`;

	// Only allow redirect if the URL contains /entity/:entityUUID (unless skipping check)
	if (!skipEntityCheck && !hasEntityUUID(pathname)) {
		return defaultPath;
	}

	if (!redirectParam) {
		return defaultPath;
	}

	// Block external URLs - only allow internal paths
	const isExternalUrl =
		/^https?:\/\//.test(redirectParam) ||
		redirectParam.startsWith('www.') ||
		redirectParam.startsWith('//');

	if (isExternalUrl) {
		console.warn(
			'Blocked external redirect attempt, only internal paths allowed',
			{
				redirectParam,
			},
		);
		return defaultPath;
	}

	// Ensure the path starts with / for internal routing
	const normalizedPath = redirectParam.startsWith('/')
		? redirectParam
		: `/${redirectParam}`;

	return `${normalizedPath}${hash}`;
};
